import { useContext, useEffect, useMemo, useState } from 'react'
import { List, Site } from '@microsoft/microsoft-graph-types'
import useAxios from '../hooks/useAxios'
import { Destination } from '../context/AxiosContext'
import { Buffer } from 'buffer'
import * as UUID from 'uuid'
import AuthorizationContext from '../context/AuthorizationContext'
import { useSharePointCache } from '../context/SharePointContext'

export type SharePointProps = {
    sharePoint: SharePoint
    setDriveId: (value) => void

    errorMessage: string
}

export type SharePoint = {
    driveId: string
    siteId: string
    webId: string
    listId: string

    site?: Site
    list?: List

    webUrl?: string
}

export default function useSharePoint(): SharePointProps {
    const axios = useAxios()
    const { host: currentHost, tokensRevision } = useContext(AuthorizationContext)
    const cache = useSharePointCache()

    const [driveId, setDriveId] = useState<string>()

    useEffect(() => {
        setDriveId(currentHost?.DriveId)
    }, [currentHost])

    // A drive id encodes the site, web and list, so it is all the identity the cache needs.
    const ids = useMemo(() => parseDriveId(driveId), [driveId])

    function callSharePoint<D>(url: string, onError: (message: string) => void): Promise<D | undefined> {
        return axios
            .get<D>(url, { baseURL: Destination.SharePoint })
            .then(response => response.data)
            .catch(error => {
                onError(error.toString())
                return undefined
            })
    }

    useEffect(() => {
        if (!ids?.listId) {
            return
        }

        // Every caller for the same drive id shares one resolution; the eleven components that use this hook used
        // to make their own pair of calls, and repeat them on every token refresh.
        cache.resolve(driveId, () => {
            let failure: string

            return callSharePoint<Site>(`/sites/${ids.siteId}`, message => (failure = `Get site: ${message}`)).then(site =>
                callSharePoint<List>(`/sites/${ids.siteId}/lists/${ids.listId}`, message => (failure = `Get list: ${message}`)).then(list => ({
                    site,
                    list,
                    webUrl: list?.webUrl,
                    errorMessage: failure,
                })),
            )
        })
    }, [driveId, ids?.siteId, ids?.listId])

    // Only what failed is retried on a fresh token: a site does not stop being that site because the token was
    // refreshed, so a successful resolution is kept.
    useEffect(() => cache.invalidateFailed(), [tokensRevision])

    const resolution = cache.get(driveId)

    const sharePoint = useMemo<SharePoint>(
        () => (ids ? { ...ids, site: resolution?.site, list: resolution?.list, webUrl: resolution?.webUrl } : undefined),
        [ids, resolution],
    )

    const errorMessage = ids ? resolution?.errorMessage : 'Er is geen geldige Drive ID ingevuld.'

    return { sharePoint, setDriveId, errorMessage }
}

const convertGuidBuffer = (bytes: Buffer): Buffer => {
    // Courtesy of https://stackoverflow.com/a/17947129/862825
    return Buffer.from([
        ...bytes.subarray(0, 4).reverse(), // time_low
        ...bytes.subarray(4, 6).reverse(), // time_mid
        ...bytes.subarray(6, 8).reverse(), // time_high
        ...bytes.subarray(8, 10), // clock
        ...bytes.subarray(10, 16), // node
    ])
}

const stringifyGuid = (bytes: Buffer): string => UUID.stringify(convertGuidBuffer(bytes))

const parseGuid = (guid: string): Uint8Array => convertGuidBuffer(UUID.parse(guid.replaceAll(/[^0-9a-fA-F-]/g, '')) as Buffer)

const parseDriveId = (driveId: string): SharePoint => {
    // Courtesy of https://www.techmikael.com/2021/01/microsoft-graph-encoding-and-decoding.html
    const bytes: Buffer = Buffer.from((driveId || '').substring(2).replaceAll('_', '/').replaceAll('-', '+'), 'base64')

    if (bytes.length !== 48) {
        return undefined
    }

    return {
        driveId,
        siteId: stringifyGuid(bytes.subarray(0, 16)),
        webId: stringifyGuid(bytes.subarray(16, 32)),
        listId: stringifyGuid(bytes.subarray(32, 48)),
    }
}

export const formatDriveId = (ids: SharePoint): string => {
    if (!ids.siteId || !ids.webId || !ids.listId) {
        return undefined
    }

    const bytes: Buffer = Buffer.from([
        ...(parseGuid(ids.siteId) as Uint8Array),
        ...(parseGuid(ids.webId) as Uint8Array),
        ...(parseGuid(ids.listId) as Uint8Array),
    ])
    return `b!${bytes.toString('base64').replaceAll('/', '_').replaceAll('+', '-')}`
}
